import {
  sendingDomainPill,
  type SendingDomainForecastDomainDto,
  type SendingDomainForecastPlanDto,
  type SendingDomainForecastViewDto,
  type SendingDomainPill,
  type SendingDomainRateLimitsDto,
  type SendingDomainRefusalCause,
} from '@/types/sendingDomains'
import {
  formatCount,
  localShortDay,
  localTimeWithZone,
  moveUpRuleSentence,
  stepLimitsSentence,
  utcDayLabel,
} from '@/utils/sendingDomainWarmup'
import { parseServerDate } from '@/utils/time'

// What the activation editor says about how its sending domain would carry the unsaved email (ORB-953).
// Every number is the forecast's own. Warm-up days are UTC days, so they are labelled by their UTC date;
// every instant (the event start, the send) is shown in the viewer's time.

// Keyed by the union, so a cause added to it has to be added here as well.
const REFUSAL_CAUSES: readonly string[] = Object.keys({
  NotVerified: true,
  WebsiteCheckNotPassed: true,
  Stopped: true,
  OverTodaysLimit: true,
} satisfies Record<SendingDomainRefusalCause, true>)

/** "events.ortec.com (Warming, step 4 of 11)", for the sending-domain select. */
export function domainOptionLabel(domain: SendingDomainForecastDomainDto, lastStep: number): string {
  const state = optionState(domain, lastStep)
  return state ? `${domain.Domain} (${state})` : domain.Domain
}

function optionState(domain: SendingDomainForecastDomainDto, lastStep: number): string | null {
  switch (sendingDomainPill(domain)) {
    case 'Ready':
      return 'Ready'
    case 'Warming':
      return domain.WarmupStage != null ? `Warming, step ${domain.WarmupStage} of ${lastStep}` : missingLimits(domain, 'Warming')
    case 'Holding':
      return domain.DailyLimit != null ? `Holding at ${formatCount(domain.DailyLimit)} a day` : missingLimits(domain, 'Holding')
    case 'Stopped':
      return 'Stopped: broadcasts held'
    case 'SettingUp': {
      const reason = settingUpReason(domain)
      return reason ? `Setting up: ${reason}` : 'Setting up'
    }
    case null:
      return null
  }
}

function missingLimits(domain: SendingDomainForecastDomainDto, state: string): string {
  console.error('A warming sending domain came without its step or limit', domain.Id)
  return state
}

/** What a setting-up domain still lacks: "website check failed". */
export function settingUpReason(domain: SendingDomainForecastDomainDto): string | null {
  if (domain.Status !== 'Verified') return 'DNS not verified'
  if (domain.WebsiteCheckStatus === 'Failed') return 'website check failed'
  if (domain.WebsiteCheckStatus === 'NotChecked') return 'website not checked yet'
  console.error('Unknown website check status on a setting-up sending domain', domain.WebsiteCheckStatus)
  return null
}

/** A setting-up domain sends nothing, so it is shown but can't be picked. */
export function isDomainSelectable(domain: SendingDomainForecastDomainDto): boolean {
  return domain.State !== 'SettingUp'
}

/** The Ready domain the editor offers to switch to: the company default if it is Ready, else the first. */
export function readyDomainToSwitchTo(forecast: SendingDomainForecastViewDto): SendingDomainForecastDomainDto | null {
  const ready = forecast.Domains.filter(domain => domain.State === 'Ready' && domain.Id !== forecast.SelectedDomain?.Id)
  return ready.find(domain => domain.IsDefault) ?? ready[0] ?? null
}

/** "Holding at 1,000 a day: hard bounces 2.4% (moves up at 2% or less)." */
export function holdingSentence(domain: SendingDomainForecastDomainDto): string | null {
  if (!domain.WarmupHoldReason || domain.DailyLimit == null) return null
  const reason = domain.WarmupHoldReason
  return `Holding at ${formatCount(domain.DailyLimit)} a day: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}.`
}

/** Beside the domain's pill: "Step 4 of 11: up to 700 a day, 50 an hour." */
export function domainStateDetail(pill: SendingDomainPill, domain: SendingDomainForecastDomainDto, lastStep: number): string | null {
  switch (pill) {
    case 'Warming':
    case 'Holding':
      return domain.WarmupStage != null && domain.DailyLimit != null
        ? stepLimitsSentence(domain.WarmupStage, lastStep, domain.DailyLimit, domain.HourlyLimit)
        : null
    case 'Ready':
      return 'Warm-up complete: no limits.'
    case 'Stopped':
      return 'Broadcasts are held until a company admin resumes it.'
    case 'SettingUp': {
      const reason = settingUpReason(domain)
      return reason ? `${reason.charAt(0).toUpperCase()}${reason.slice(1)}.` : null
    }
  }
}

/** Under it: how a warming domain moves up, or why it is holding. */
export function domainRuleLine(pill: SendingDomainPill, domain: SendingDomainForecastDomainDto, rateLimits: SendingDomainRateLimitsDto): string | null {
  if (pill === 'Holding') return holdingSentence(domain)
  if (pill === 'Warming' && domain.QualifyingVolume != null) return moveUpRuleSentence(domain.QualifyingVolume, rateLimits)
  return null
}

/** A broadcast the warm-up spreads across days, or cuts off at the event start. */
export function isSpreadByWarmup(plan: SendingDomainForecastPlanDto | null | undefined): plan is SendingDomainForecastPlanDto {
  return !!plan && (plan.Days.length > 1 || plan.AfterDeadline > 0)
}

/** "Wed 30 Sep, 16:00 CEST": the event start in the viewer's time. */
function eventStart(forecast: SendingDomainForecastViewDto): string {
  if (!forecast.EventStartUtc) {
    console.error('A sending-domain forecast cut off at the event start carries no event start')
    return ''
  }
  return `${localShortDay(forecast.EventStartUtc)}, ${localTimeWithZone(forecast.EventStartUtc)}`
}

function finishDay(plan: SendingDomainForecastPlanDto): string {
  if (!plan.FinishDayUtc) {
    console.error('A sending-domain forecast that reaches everyone carries no finish day')
    return ''
  }
  return utcDayLabel(plan.FinishDayUtc)
}

/** "2,180 of 7,380 won't get this before the event starts", or "Goes out over 4 days, finishing Tue 29 Sep". */
export function spreadHeadline(plan: SendingDomainForecastPlanDto): string {
  if (plan.AfterDeadline > 0) {
    const total = plan.BeforeDeadline + plan.AfterDeadline
    return `${formatCount(plan.AfterDeadline)} of ${formatCount(total)} won't get this before the event starts`
  }
  return `Goes out over ${plan.Days.length} days, finishing ${finishDay(plan)}`
}

/** The line under the headline: how many get it in time, or that the finish can slip. */
export function spreadDetail(forecast: SendingDomainForecastViewDto, plan: SendingDomainForecastPlanDto): string | null {
  if (plan.AfterDeadline === 0) {
    return 'The daily limit goes up each day the domain stays healthy. If it holds at a step, this finishes later.'
  }
  if (plan.BeforeDeadline === 0) return null
  const expected = `${formatCount(plan.BeforeDeadline)} ${plan.BeforeDeadline === 1 ? 'is' : 'are'} expected to get it by ${eventStart(forecast)}.`
  return plan.Days.length > 1
    ? `The daily limit goes up each day the domain stays healthy, so ${expected}`
    : expected.charAt(0).toUpperCase() + expected.slice(1)
}

export interface SpreadLegendDay {
  dayStartUtc: string
  /** "Wed 30 Sep until 16:00 CEST" on the day the event starts. */
  label: string
  expected: number
}

export function spreadLegendDays(plan: SendingDomainForecastPlanDto): SpreadLegendDay[] {
  return plan.Days.map(day => ({
    dayStartUtc: day.DayStartUtc,
    label: day.EndsAtUtc
      ? `${utcDayLabel(day.DayStartUtc)} until ${localTimeWithZone(day.EndsAtUtc)}`
      : utcDayLabel(day.DayStartUtc),
    expected: day.Expected,
  }))
}

/** The "Will fire" box's second line: "Spread over 4 days by the daily limit, until the event starts Wed 30 Sep, 16:00 CEST." */
export function willFireSpreadLine(forecast: SendingDomainForecastViewDto): string | null {
  const plan = forecast.Forecast
  if (!isSpreadByWarmup(plan)) return null
  if (plan.AfterDeadline === 0) {
    return `Spread over ${plan.Days.length} days by the daily limit, finishing ${finishDay(plan)}.`
  }
  return plan.Days.length > 1
    ? `Spread over ${plan.Days.length} days by the daily limit, until the event starts ${eventStart(forecast)}.`
    : `Sends at the domain's warm-up limit until the event starts ${eventStart(forecast)}.`
}

/** Beside Save when a broadcast would leave people out: "Saves with 2,180 not sent." */
export function saveConsequence(forecast: SendingDomainForecastViewDto): string | null {
  const plan = forecast.Forecast
  return plan && plan.AfterDeadline > 0 ? `Saves with ${formatCount(plan.AfterDeadline)} not sent.` : null
}

/** The refusal's cause, or null (logged) for a cause this build does not know. */
function refusalCause(forecast: SendingDomainForecastViewDto): SendingDomainRefusalCause | null {
  if (!forecast.RefusalCause) return null
  if (!REFUSAL_CAUSES.includes(forecast.RefusalCause)) {
    console.error('Unknown sending domain refusal cause', forecast.RefusalCause)
    return null
  }
  return forecast.RefusalCause as SendingDomainRefusalCause
}

/** "This email can't be saved with events.ortec.com". */
export function refusalHeadline(forecast: SendingDomainForecastViewDto): string {
  return `This email can't be saved with ${forecast.SelectedDomain?.Domain ?? 'the selected sending domain'}`
}

/** Beside the disabled Save: "Can't save with events.ortec.com. Choose a Ready domain." */
export function saveBlockedReason(forecast: SendingDomainForecastViewDto): string {
  const domain = forecast.SelectedDomain?.Domain ?? 'the selected sending domain'
  const advice = refusalCause(forecast) === 'OverTodaysLimit' ? 'Choose a Ready domain.' : 'Choose another domain.'
  return `Can't save with ${domain}. ${advice}`
}

function sameInstant(a: string | null | undefined, b: string | null | undefined): boolean {
  const first = parseServerDate(a)
  const second = parseServerDate(b)
  return !!first && !!second && first.getTime() === second.getTime()
}

/**
 * Why a send timed to the event does not fit a warming domain, with the times Save's own message leaves
 * out: "events.ortec.com is Warming: 50 emails an hour today, and at most 150 an hour by Wed 30 Sep. This
 * email has to reach 7,380 people within the hour the event starts (16:00 CEST), and it can't wait for
 * later hours or days." Null for every other refusal, which shows the server's reason as it is.
 */
export function timedSendRefusal(forecast: SendingDomainForecastViewDto): string | null {
  const domain = forecast.SelectedDomain
  if (refusalCause(forecast) !== 'OverTodaysLimit' || !domain || domain.DailyLimit == null || forecast.Recipients == null) {
    return null
  }

  const today = domain.HourlyLimit != null
    ? `${formatCount(domain.HourlyLimit)} emails an hour today`
    : `${formatCount(domain.DailyLimit)} emails today`

  // The send day's limits assume a step up every day from today, so they are the most it could carry.
  const sendDay = forecast.SendDayLimits
  const sendDayLimit = sendDay?.HourlyLimit != null
    ? `${formatCount(sendDay.HourlyLimit)} an hour`
    : sendDay?.DailyLimit != null ? `${formatCount(sendDay.DailyLimit)} a day` : null
  const later = sendDay && sendDay.Step != null && sendDay.Step !== domain.WarmupStage && sendDayLimit
    ? `, and at most ${sendDayLimit} by ${utcDayLabel(sendDay.DayStartUtc)}`
    : ''

  const moment = sameInstant(forecast.SendAtUtc, forecast.EventStartUtc) ? 'the event starts' : 'it sends'
  const at = forecast.SendAtUtc ? ` (${localTimeWithZone(forecast.SendAtUtc)})` : ''
  const deadline = domain.HourlyLimit != null
    ? `within the hour ${moment}${at}, and it can't wait for later hours or days`
    : `when ${moment}${at}, and it can't wait for another day`

  return `${domain.Domain} is Warming: ${today}${later}. This email has to reach ${formatCount(forecast.Recipients)} people ${deadline}.`
}
